import { HOTKEY_LABEL, HOTKEY_TITLE } from './useGlobalSearchHotkey';
import React from 'react';
import { Icon, mergeStyleSets, Text } from '@fluentui/react';

interface IGlobalSearchTriggerProps {
    /** Icon-only below the compact breakpoint so the CommandBar never overflow-hides the trigger. */
    compact: boolean;
    onOpen: () => void;
}

const classes = mergeStyleSets({
    pill: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5em',
        height: 32,
        margin: '0 8px',
        padding: '0 14px',
        border: '1px solid #d1d1d1',
        borderRadius: 16,
        backgroundColor: '#faf9f8',
        cursor: 'pointer',
        selectors: {
            '&:hover': { borderColor: '#999', backgroundColor: '#fff' },
        },
    },
    glyph: { fontSize: 14, color: '#789c2f' },  // themePrimary green (App.tsx)
    label: { color: '#323130', whiteSpace: 'nowrap' },
    kbd: {
        flexShrink: 0,
        fontSize: 10,
        color: '#5a7523',
        border: '1px solid #a8bf78',
        borderRadius: 3,
        padding: '1px 5px',
        backgroundColor: '#f3f7e8',
    },
});

/**
 * The header entry point for the global search palette: a "Search" magnifier pill.
 * Deliberately not a live input — clicking it opens the palette, whose own SearchBox is the only
 * text box the user types in, so there's never a second focused input racing the overlay.
 * Rendered as a div, not a button: the CommandBar item hosting it is already a button (same
 * posture as TasksDropdown).
 */
const GlobalSearchTrigger: React.FC<IGlobalSearchTriggerProps> = (props) => {
    if (props.compact) {
        return (
            <div
                onClick={props.onOpen}
                title={HOTKEY_TITLE}
                aria-label={HOTKEY_TITLE}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', height: '100%', padding: '0 8px' }}
            >
                <Icon iconName='Search' className={classes.glyph} style={{ fontSize: 16 }} />
            </div>
        );
    }

    return (
        <div
            className={classes.pill}
            aria-label={HOTKEY_TITLE}
            onClick={props.onOpen}
        >
            <Icon iconName='Search' className={classes.glyph} />
            <Text variant='medium' className={classes.label}>Search</Text>
            <span className={classes.kbd}>{HOTKEY_LABEL}</span>
        </div>
    );
};

export default GlobalSearchTrigger;
